if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.memerlePersonalizationFields = this.form.querySelectorAll('[data-memerle-personalization-required]');
        this.memerleDateFields = this.form.querySelectorAll('[data-memerle-date-required]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.memerleDateFields.length > 0) {
          this.memerleDateFields.forEach((field) => {
            field.addEventListener('input', this.onMemerleDateInput.bind(this));
          });
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (!this.validateMemerlePersonalization()) {
          return;
        }

        if (!this.validateMemerleVariantOptions()) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /* Memerle — messages d'erreur dans la langue de la page (FR / EN) */
      memerleT(key, label) {
        const en = (document.documentElement.lang || '').toLowerCase().indexOf('en') === 0;
        const messages = en
          ? {
              field: 'Field',
              option: 'Option',
              fill: `Please fill in “${label}” before adding to cart.`,
              date_format: 'Please enter the date as DD/MM/YY (e.g. 12/07/26).',
              date_invalid: 'The event date looks invalid. Please use the DD/MM/YY format.',
              choose: `Please choose a “${label}” before adding to cart.`,
            }
          : {
              field: 'Champ',
              option: 'Option',
              fill: `Veuillez remplir « ${label} » avant d’ajouter au panier.`,
              date_format: 'Veuillez saisir la date au format JJ/MM/AA (exemple : 12/07/26).',
              date_invalid: 'La date de l’événement semble invalide. Utilisez le format JJ/MM/AA.',
              choose: `Veuillez choisir une valeur pour « ${label} » avant d’ajouter au panier.`,
            };
        return messages[key];
      }

      validateMemerlePersonalization() {
        const fields = this.memerlePersonalizationFields;
        if (fields.length === 0) return true;

        for (const el of fields) {
          const raw = el.value != null ? String(el.value) : '';
          const value = raw.trim();
          if (!value) {
            const lab = this.form.querySelector(`label[for="${el.id}"]`);
            const labelText = lab?.textContent?.trim() || this.memerleT('field');
            this.showRequiredFieldError(this.memerleT('fill', labelText));
            el.focus({ preventScroll: false });
            try {
              el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            } catch (e) {
              el.scrollIntoView({ block: 'center' });
            }
            return false;
          }

          if (el.hasAttribute('data-memerle-date-required')) {
            const mm = value.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
            if (!mm) {
              this.showRequiredFieldError(this.memerleT('date_format'));
              el.focus({ preventScroll: false });
              return false;
            }
            const day = Number(mm[1]);
            const month = Number(mm[2]);
            if (month < 1 || month > 12 || day < 1 || day > 31) {
              this.showRequiredFieldError(this.memerleT('date_invalid'));
              el.focus({ preventScroll: false });
              return false;
            }
          }
        }
        return true;
      }

      /**
       * Options type « Finition » (pilules / liste) : une valeur doit être choisie par groupe.
       * Le markup Dawn place les sélecteurs hors du form ; on lit variant-selects dans la même section produit.
       */
      validateMemerleVariantOptions() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return true;

        const productInfo =
          document.getElementById(`MainProduct-${sectionId}`) ||
          this.closest('[id^="MainProduct-"]') ||
          this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!variantSelects) return true;

        const fieldsets = variantSelects.querySelectorAll(
          'fieldset.product-form__input--pill, fieldset.product-form__input--swatch'
        );
        for (const fs of fieldsets) {
          const radios = fs.querySelectorAll('input[type="radio"]:not(.disabled):not([disabled])');
          if (radios.length === 0) continue;

          const checked = fs.querySelector('input[type="radio"]:checked:not(.disabled)');
          if (!checked) {
            const legend = fs.querySelector('legend.form__label');
            const optName = legend?.textContent?.replace(/\s*:\s*$/, '').trim() || this.memerleT('option');
            this.showRequiredFieldError(this.memerleT('choose', optName));
            try {
              fs.scrollIntoView({ block: 'center', behavior: 'smooth' });
            } catch (e) {
              fs.scrollIntoView({ block: 'center' });
            }
            return false;
          }
        }
        return true;
      }

      showRequiredFieldError(message) {
        const wrap = this.querySelector('.product-form__error-message-wrapper');
        const textEl = wrap?.querySelector('.product-form__error-message');
        if (!wrap || !textEl) return;
        wrap.removeAttribute('hidden');
        textEl.textContent = message;
      }

      onMemerleDateInput(evt) {
        const input = evt?.target;
        if (!input) return;
        const digits = String(input.value || '')
          .replace(/\D/g, '')
          .slice(0, 6);

        if (digits.length <= 2) {
          input.value = digits;
          return;
        }

        if (digits.length <= 4) {
          input.value = `${digits.slice(0, 2)}/${digits.slice(2)}`;
          return;
        }

        input.value = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 6)}`;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
